import Grid from '@mui/material/Grid';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete';
import { RHFSwitchField } from '@openx/components-core/lib/SwitchField/RHFSwitchField';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDealContext } from '../../../utils/context/DealContext';
import { FiltersDropdown } from '../components/FiltersDropdown';
import { SectionTitle } from '../components/SectionTitle';
import { SelectedFilters } from '../components/SelectedFilters';
import {
  Filters,
  getFilterOptions,
  SupplementalAdQualityField,
  type SupplementalAdQualityFormState,
} from '../constants';

export const BrandsSection = () => {
  const { useBrandsFetch } = useDealContext();

  if (!useBrandsFetch) {
    return null;
  }

  return <BrandsSectionRender useBrandsFetch={useBrandsFetch} />;
};

export const BrandsSectionRender = ({ useBrandsFetch }) => {
  const { setValue, control } = useFormContext<SupplementalAdQualityFormState>();

  const observableBrandsState = useWatch({ control, name: 'brands' });
  const brandOp = observableBrandsState?.op ?? 'current';
  const brandIds = observableBrandsState?.ids ?? [];

  const { t } = useTranslation();
  const filterOptions = useMemo(() => getFilterOptions(t), [t]);
  const { loading: isBrandsLoading, mapById: brands = {}, refetch: refetchBrands } = useBrandsFetch([...brandIds]);

  const handleFilterTypeChange = useCallback(
    (_event, value) => {
      setValue(SupplementalAdQualityField.BRANDS_IDS, []);
      setValue(SupplementalAdQualityField.BRANDS_OP, value);
    },
    [setValue]
  );

  const handleChange = useCallback(
    (value: string[]) => {
      setValue(SupplementalAdQualityField.BRANDS_IDS, value);
    },
    [setValue]
  );

  return (
    <>
      <SectionTitle title={t('Brands')} />
      <Grid container spacing={1}>
        <Grid size={6}>
          <RHFAutoComplete
            name={SupplementalAdQualityField.BRANDS_OP}
            textFieldProps={{ margin: 'dense' }}
            disableClearable={true}
            getOptionLabel={option => filterOptions[option]?.name || option}
            options={Object.keys(filterOptions)}
            onChange={handleFilterTypeChange}
            fullWidth
            data-test="saq-brands"
          />
        </Grid>
        {brandOp === Filters.ALLOW && (
          <Grid size={6}>
            <FiltersDropdown
              options={brands}
              loading={!!isBrandsLoading}
              selectedOptions={brandIds}
              placeholder={t('[ Search A Brand ]')}
              onChange={handleChange}
              fetchOptions={refetchBrands}
              debounced
              dataTest="brands-search"
            />
          </Grid>
        )}
      </Grid>
      {brandIds.length > 0 && (
        <SelectedFilters
          selectedOptions={brandIds}
          handleChange={handleChange}
          options={brands}
          title={`${t('Brands')} [ ${brandIds.length} ]`}
          filtersTitle={filterOptions.allow.name}
        />
      )}
      <RHFSwitchField
        name={SupplementalAdQualityField.UNBRANDED_RTB}
        label={t('Allow Unbranded RTB Bids')}
        type="checkbox"
        data-test="saq-unbranded-rtb"
      />
    </>
  );
};
